import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { CATEGORIES, MENU, EXTRAS, WHATSAPP } from "../../data";
import { Reveal } from "./Reveal";

const orderLink = (name) =>
    `${WHATSAPP}?text=${encodeURIComponent(`¡Hola! Quiero pedir un/a ${name} 🍓`)}`;

const OrderButton = ({ name }) => (
    <motion.a
        href={orderLink(name)}
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        data-testid={`order-btn-${name.toLowerCase().replace(/[^a-z]/g, "")}`}
        className="mt-4 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-display font-semibold text-sm px-4 py-2.5 rounded-full shadow-md shadow-black/10"
    >
        <MessageCircle size={16} /> Pedir ahora
    </motion.a>
);

const ProductCard = ({ item, index }) => (
    <motion.div
        layout
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ delay: index * 0.06, duration: 0.5 }}
        whileHover={{ y: -8 }}
        data-testid={`menu-card-${item.name.toLowerCase().replace(/[^a-z]/g, "")}`}
        className="group relative bg-white rounded-3xl overflow-hidden shadow-xl shadow-hotpink/10 border border-pink-50 flex flex-col"
    >
        <div className="relative aspect-square overflow-hidden bg-gradient-to-b from-lightpink/25 to-white">
            <img
                src={item.image}
                alt={item.name}
                loading="lazy"
                className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute top-3 right-3 bg-strawred text-white font-display font-bold text-lg px-3.5 py-1 rounded-full shadow-md shadow-strawred/30">
                {item.price}
            </div>
        </div>
        <div className="p-5 md:p-6 flex flex-col flex-1">
            <h3 className="font-display font-bold text-xl md:text-2xl text-[#1a0a10] leading-tight">
                {item.name}{" "}
                <span className="whitespace-nowrap">{item.emoji}</span>
            </h3>
            <p className="mt-2 text-[#6e4b58] font-medium text-sm md:text-base flex-1">
                {item.desc}
            </p>
            {item.meta && (
                <p className="mt-2 text-xs text-hotpink font-semibold font-label uppercase tracking-wide">
                    {item.meta}
                </p>
            )}
            <OrderButton name={item.name} />
        </div>
    </motion.div>
);

const SmoothieCard = ({ item, index }) => (
    <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ delay: index * 0.04, duration: 0.4 }}
        whileHover={{ y: -8 }}
        data-testid={`smoothie-card-${index + 1}`}
        className="group relative bg-white rounded-3xl overflow-hidden shadow-lg shadow-hotpink/10 border border-pink-50 flex flex-col"
    >
        <div className="relative aspect-square overflow-hidden bg-gradient-to-b from-lightpink/25 to-white">
            <img
                src={item.image}
                alt={item.name}
                loading="lazy"
                className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
            />
            <span className="absolute top-3 left-3 w-8 h-8 grid place-items-center rounded-full bg-hotpink text-white font-display font-bold text-sm shadow-md">
                {index + 1}
            </span>
            <div className="absolute top-3 right-3 bg-strawred text-white font-display font-bold text-sm px-3 py-1 rounded-full shadow-md shadow-strawred/30">
                {item.price}
            </div>
        </div>
        <div className="p-4 md:p-5 flex flex-col flex-1">
            <h4 className="font-display font-bold text-base md:text-lg text-[#1a0a10]">
                {item.name}
            </h4>
            <p className="text-[#6e4b58] text-sm font-medium flex-1">
                {item.desc}
            </p>
            <OrderButton name={item.name} />
        </div>
    </motion.div>
);

export const MenuSection = () => {
    const [active, setActive] = useState("fresas-crema");
    const data = MENU[active];
    const isSmoothie = active === "smoothies";

    return (
        <section
            id="menu"
            data-testid="menu-section"
            className="relative py-20 md:py-28 px-4"
        >
            <div className="max-w-6xl mx-auto">
                <Reveal className="text-center">
                    <span className="font-label uppercase tracking-[0.25em] text-xs text-strawred font-semibold">
                        La Carta
                    </span>
                    <h2 className="mt-3 font-display font-bold text-4xl md:text-6xl text-gradient-pink">
                        Nuestra Carta 🍓
                    </h2>
                    <p className="mt-3 text-[#6e4b58] font-medium text-lg">
                        Tú eliges, nosotros creamos ✨
                    </p>
                </Reveal>

                {/* Tabs / filter pills */}
                <div
                    data-testid="menu-tabs"
                    className="mt-10 flex flex-wrap justify-center gap-2.5 md:gap-3"
                >
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`tab-${c.id}`}
                            onClick={() => setActive(c.id)}
                            className={`font-label uppercase tracking-wide text-xs md:text-sm font-semibold px-4 md:px-5 py-2.5 rounded-full transition-colors border-2 ${
                                active === c.id
                                    ? "bg-hotpink text-white border-hotpink shadow-lg shadow-hotpink/30"
                                    : "bg-white text-[#6e4b58] border-pink-100 hover:border-hotpink hover:text-hotpink"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {/* Note / badges */}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                    {data.note && (
                        <span
                            data-testid="menu-note"
                            className="inline-flex items-center gap-2 bg-lightpink/40 text-hotpink font-semibold text-sm px-4 py-2 rounded-full border border-hotpink/20"
                        >
                            {data.note}
                        </span>
                    )}
                    {data.badge && (
                        <span className="inline-flex items-center gap-2 bg-strawred/10 text-strawred font-semibold text-sm px-4 py-2 rounded-full border border-strawred/20">
                            {data.badge}
                        </span>
                    )}
                    {data.price && (
                        <span className="inline-flex items-center gap-2 bg-strawred text-white font-display font-bold text-sm px-4 py-2 rounded-full">
                            {data.price}
                        </span>
                    )}
                </div>

                {/* Cards */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active}
                        className={
                            isSmoothie
                                ? "mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
                                : "mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                        }
                    >
                        {data.items.map((item, i) =>
                            isSmoothie ? (
                                <SmoothieCard
                                    key={item.name}
                                    item={item}
                                    index={i}
                                />
                            ) : (
                                <ProductCard
                                    key={item.name}
                                    item={item}
                                    index={i}
                                />
                            ),
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* Extras — always visible */}
                <Reveal className="mt-16">
                    <div className="bg-white rounded-3xl p-6 md:p-9 shadow-xl shadow-hotpink/10 border border-pink-50">
                        <h3 className="font-display font-bold text-2xl md:text-3xl text-[#1a0a10] text-center">
                            Extras 🎨
                        </h3>
                        <div
                            data-testid="extras-pills"
                            className="mt-6 flex flex-wrap justify-center gap-3"
                        >
                            {EXTRAS.map((e) => (
                                <span
                                    key={e.label}
                                    className="inline-flex items-center gap-2 bg-lightpink/30 border border-hotpink/20 rounded-full pl-4 pr-1.5 py-1.5 font-medium text-sm text-[#1a0a10]"
                                >
                                    {e.label}
                                    <span className="bg-strawred text-white font-bold text-xs px-3 py-1 rounded-full">
                                        {e.price}
                                    </span>
                                </span>
                            ))}
                        </div>
                        <p className="mt-6 text-center font-display font-medium text-hotpink text-lg">
                            Tú eliges los toppings y salsas, nosotros hacemos la
                            magia ✨
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
